import React, { useState } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { API_URL } from '../../../config/api';
import styles from './ActionModals.module.scss';

/**
 * 📧 SEND EMAIL MODAL - Modal para Enviar Emails
 * 
 * @param {boolean} show - Controla visibilidade
 * @param {Function} onHide - Callback ao fechar
 * @param {number} saleId - ID da venda
 * @param {Object} sale - Dados da venda
 * @param {Function} onSuccess - Callback após sucesso
 */
const SendEmailModal = ({ show, onHide, saleId, sale, onSuccess }) => {
  const [emailType, setEmailType] = useState('receipt');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const emailTypes = [
    {
      value: 'receipt',
      label: 'Comprovante de Compra',
      icon: 'mdi:receipt',
      description: 'Email com detalhes da compra e comprovante',
      color: '#10b981'
    },
    {
      value: 'checkout',
      label: 'Link de Pagamento',
      icon: 'mdi:link-variant',
      description: 'Email com link para finalizar pagamento',
      color: '#3b82f6'
    }
  ];

  const selectedType = emailTypes.find(t => t.value === emailType);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/sales/${saleId}/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email_type: emailType })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao enviar email');
      }

      console.log('✅ Email enviado com sucesso');
      setSuccess(true);

      // Aguardar 2s e fechar
      setTimeout(() => {
        setEmailType('receipt');
        setSuccess(false);
        onHide();
        
        if (onSuccess) {
          onSuccess();
        }
      }, 2000);

    } catch (err) {
      console.error('❌ Erro ao enviar email:', err);
      setError(err.message || 'Erro ao enviar email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="md"
      backdrop={false}
      className={styles.actionModal}
      enforceFocus={false}
    >
      <Modal.Header closeButton className={styles.headerEmail}>
        <Modal.Title as="span" className={styles.modalTitle}>
          <Icon icon="mdi:email-send" />
          Enviar Email para Cliente
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className={styles.modalBody}>
          {error && (
            <Alert variant="danger" className="mb-3">
              <Icon icon="mdi:alert-circle" className="me-2" />
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" className="mb-3">
              <Icon icon="mdi:check-circle" className="me-2" />
              Email enviado com sucesso para {sale?.customer_email}!
            </Alert>
          )}

          {/* Email do Cliente */}
          <Alert variant="info" className="mb-3">
            <div className="d-flex align-items-center gap-2">
              <Icon icon="mdi:email" style={{ fontSize: '1.5rem' }} />
              <div>
                <strong>Destinatário:</strong><br/>
                {sale?.customer_name} ({sale?.customer_email})
              </div>
            </div>
          </Alert>

          {/* Tipo de Email */}
          <Form.Group>
            <Form.Label>
              <Icon icon="mdi:email-variant" className="me-2" />
              Tipo de Email *
            </Form.Label>
            
            {emailTypes.map(type => (
              <div
                key={type.value}
                className={`${styles.emailTypeOption} ${emailType === type.value ? styles.selected : ''}`}
                onClick={() => !loading && setEmailType(type.value)}
              >
                <div className={styles.emailTypeRadio}>
                  <Form.Check
                    type="radio"
                    id={`email-type-${type.value}`}
                    name="email-type"
                    value={type.value}
                    checked={emailType === type.value}
                    onChange={(e) => setEmailType(e.target.value)}
                    disabled={loading}
                  />
                </div>
                
                <div className={styles.emailTypeIcon} style={{ backgroundColor: type.color }}>
                  <Icon icon={type.icon} />
                </div>
                
                <div className={styles.emailTypeContent}>
                  <strong>{type.label}</strong>
                  <small>{type.description}</small>
                </div>
              </div>
            ))}
          </Form.Group>

          {/* Preview */}
          {selectedType && (
            <Alert variant="light" className="mt-3">
              <small>
                <strong>Preview:</strong> O cliente receberá um email formatado com {
                  emailType === 'receipt' ? 'detalhes da compra e comprovante' : 'link direto para finalizar o pagamento'
                }.
              </small>
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer className={styles.modalFooter}>
          <Button
            variant="outline-secondary"
            onClick={onHide}
            disabled={loading}
          >
            <Icon icon="mdi:close" className="me-2" />
            Cancelar
          </Button>
          
          <Button
            variant="primary"
            type="submit"
            disabled={loading || success}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Enviando...
              </>
            ) : success ? (
              <>
                <Icon icon="mdi:check" className="me-2" />
                Enviado!
              </>
            ) : (
              <>
                <Icon icon="mdi:send" className="me-2" />
                Enviar Email
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SendEmailModal;

